import React, { useEffect, useState } from 'react';
import { Radio, Users, ChevronRight, Loader2, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { getCategoryColor } from '../utils/categoryColors';

/**
 * DATAPRO "Full Stage" — reusable card for an active LIVE session.
 *
 * Renders the design-blueprint pattern: solid red top border, pulsing
 * "LIVE" badge, big tabular running clock (elapsed since the session was
 * first detected), category pill, driver count and a primary CTA to jump
 * into the analysis.
 *
 * Data source: the race object as stored in Mongo (via /api/races). A
 * race is "live" when `source === 'speedhive_live_auto'` and its
 * `status` is not "finalized" — this component is a pure presentation
 * layer and does not make any network calls.
 */

const pad2 = (n) => String(n).padStart(2, '0');

function formatElapsed(ms) {
  if (!Number.isFinite(ms) || ms < 0) return '00:00:00';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}

const LiveSessionCard = ({ race, onSelectRace, loading }) => {
  const { t } = useTranslation();
  const startedAt = race.created_at ? new Date(race.created_at) : null;
  const [now, setNow] = useState(() => Date.now());

  // 1s clock — cheap; we're the only thing on screen using it.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const elapsedMs = startedAt ? now - startedAt.getTime() : 0;
  const catColor = getCategoryColor(race.category || race.speedhive_group_name || '');
  const sessionLabel = [race.speedhive_group_name, race.speedhive_session_name]
    .filter(Boolean)
    .join(' · ') || race.name;

  return (
    <div
      data-testid={`live-session-card-${race.id}`}
      className="relative overflow-hidden rounded-lg transition-shadow hover:shadow-lg active:scale-[0.99]"
      style={{
        backgroundColor: 'var(--dp-surface-0)',
        border: '1px solid var(--dp-border)',
        borderTop: '4px solid var(--dp-primary)',
      }}
    >
      {/* Live corner accent */}
      <div
        aria-hidden="true"
        className="absolute -right-8 -top-8 w-24 h-24 rounded-full opacity-10"
        style={{ background: 'radial-gradient(circle, var(--dp-primary), transparent 70%)' }}
      />

      <div className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4">
        {/* LEFT — badge + labels */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span
              data-testid="live-session-badge"
              className="inline-flex items-center gap-1.5 rounded-full bg-red-600/15 border border-red-600/40 px-2.5 py-1 min-h-[28px] text-[11px] font-bold uppercase tracking-wider text-red-500"
            >
              <span className="dp-live-dot inline-block h-2 w-2 rounded-full bg-red-500" />
              <Radio className="h-3 w-3" />
              {t('nav.live')}
            </span>
            {race.category && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase font-semibold ${catColor.bg} ${catColor.text} border ${catColor.border}`}
                data-testid="live-session-category"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${catColor.dot}`} />
                {race.category.replace(/_/g, ' ')}
              </span>
            )}
          </div>

          <h3
            className="text-lg md:text-xl font-bold leading-tight truncate"
            style={{ color: 'var(--dp-text-primary)' }}
            data-testid="live-session-title"
          >
            {sessionLabel}
          </h3>

          {race.speedhive_event_name && (
            <p
              className="text-xs md:text-sm mt-0.5 truncate"
              style={{ color: 'var(--dp-text-secondary)' }}
              data-testid="live-session-event"
            >
              <Flag className="inline-block w-3 h-3 mr-1 -mt-0.5" />
              {race.speedhive_event_name}
            </p>
          )}

          <div
            className="mt-2 flex items-center gap-3 text-xs"
            style={{ color: 'var(--dp-text-muted)' }}
          >
            <span
              className="inline-flex items-center gap-1"
              data-testid="live-session-driver-count"
            >
              <Users className="w-3.5 h-3.5" />
              <span className="tabular-metric">{race.driver_count ?? 0}</span>
            </span>
            {startedAt && (
              <>
                <span aria-hidden>·</span>
                <span data-testid="live-session-started-at">
                  {startedAt.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </>
            )}
          </div>
        </div>

        {/* RIGHT — countdown + CTA */}
        <div className="flex items-center gap-3 md:flex-col md:items-end md:gap-2">
          <div className="text-right">
            <p
              className="text-[10px] uppercase font-semibold tracking-wider"
              style={{ color: 'var(--dp-text-muted)' }}
            >
              {t('live_card.elapsed')}
            </p>
            <p
              data-testid="live-session-elapsed"
              className="text-2xl md:text-3xl font-bold tabular-metric leading-none"
              style={{ color: 'var(--dp-text-primary)' }}
            >
              {formatElapsed(elapsedMs)}
            </p>
          </div>

          <Button
            onClick={() => onSelectRace(race)}
            disabled={loading}
            data-testid={`live-session-analyze-${race.id}`}
            className="h-12 min-h-[48px] min-w-[48px] bg-red-600 hover:bg-red-700 text-white active:scale-95 shadow-md whitespace-nowrap"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                {t('live_card.analyze')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LiveSessionCard;
